import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowLeft, ArrowRight, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { QUIZ_STEPS, scoreAnswers, PACKAGE_RESULTS } from "@/data/packageQuiz";

const API = process.env.REACT_APP_BACKEND_URL;
const ease = [0.22, 1, 0.36, 1];

const readUtm = () => {
  const p = new URLSearchParams(window.location.search);
  const utm = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
    if (p.get(k)) utm[k] = p.get(k);
  });
  return Object.keys(utm).length ? utm : null;
};

const OptionCard = ({ selected, label, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`group w-full text-left rounded-xl border px-5 py-4 flex items-center gap-4 transition-all duration-300 ${
      selected
        ? "border-white bg-white/[0.07]"
        : "border-white/[0.1] bg-white/[0.015] hover:border-white/30 hover:bg-white/[0.04]"
    }`}
  >
    <span
      className={`grid place-items-center h-5 w-5 rounded-full border shrink-0 transition-colors ${
        selected ? "bg-white border-white text-black" : "border-white/25 text-transparent"
      }`}
    >
      <Check size={12} strokeWidth={3} />
    </span>
    <span className={`text-[14.5px] leading-snug ${selected ? "text-white" : "text-white/75"}`}>{label}</span>
  </button>
);

const QuizForm = ({ answers, result, onDone }) => {
  const [f, setF] = useState({ full_name: "", company: "", email: "", phone: "", website: "" });
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const valid = f.full_name.trim() && f.company.trim() && /\S+@\S+\.\S+/.test(f.email) && f.phone.trim().length >= 6;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return toast.error("Please fill in your name, company, email and phone.");
    setSending(true);
    try {
      const res = await fetch(`${API}/api/audit-leads/qualify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: f.full_name.trim(),
          company: f.company.trim(),
          email: f.email.trim(),
          phone: f.phone.trim(),
          website: f.website.trim() || null,
          answers,
          score: result.score,
          recommended_package: PACKAGE_RESULTS[result.packageKey].name,
          marketing_consent: consent,
          utm: readUtm(),
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      onDone();
    } catch {
      toast.error("Something went wrong. Please try again.");
      setSending(false);
    }
  };

  const field = (name, label, type = "text", optional = false) => (
    <div>
      <label className="block font-mono-grotesk text-[10.5px] tracking-[0.18em] uppercase text-white/45 mb-2">
        {label} {optional && <span className="text-white/25 normal-case tracking-normal">(optional)</span>}
      </label>
      <input
        data-testid={`qual-form-${name}`}
        type={type}
        value={f[name]}
        onChange={set(name)}
        className="w-full rounded-lg border border-white/12 bg-white/[0.03] px-4 h-12 text-[14.5px] text-white placeholder-white/25 outline-none focus:border-white/40 transition-colors"
      />
    </div>
  );

  return (
    <motion.form
      data-testid="qual-form"
      onSubmit={submit}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease }}
      className="mt-10 max-w-xl mx-auto text-left"
    >
      <p className="font-title text-xl sm:text-2xl font-medium text-white text-center tracking-tight">
        Get your personalised website audit
      </p>
      <p className="mt-2 mb-7 text-[14px] text-white/55 text-center leading-relaxed">
        No need to re-answer anything — just your details and I'll record a personal audit for you.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        {field("full_name", "Full name")}
        {field("company", "Company")}
        {field("email", "Business email", "email")}
        {field("phone", "Phone", "tel")}
      </div>
      <div className="mt-4">{field("website", "Current website", "text", true)}</div>

      <label className="mt-5 flex items-start gap-3 cursor-pointer">
        <input
          data-testid="qual-form-consent"
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 h-4 w-4 accent-white"
        />
        <span className="text-[12.5px] text-white/45 leading-relaxed">
          Keep me updated with occasional tips and offers. (Optional — sending this form does not sign you up.)
        </span>
      </label>
      <p className="mt-3 flex items-start gap-2 text-[11.5px] text-white/35 leading-relaxed">
        <ShieldCheck size={14} className="mt-0.5 shrink-0" />
        <span>
          Your details are used only to prepare and send your audit. Read the{" "}
          <a href="/privacy" className="underline underline-offset-2 hover:text-white/60">privacy policy</a>.
        </span>
      </p>

      <button
        type="submit"
        data-testid="qual-form-submit"
        disabled={sending}
        className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-black h-13 py-3.5 px-7 text-[14px] font-medium hover:bg-white/90 transition-colors disabled:opacity-60"
      >
        {sending ? <Loader2 size={16} className="animate-spin" /> : null}
        Get My Personalised Website Audit
      </button>
    </motion.form>
  );
};

export const PackageQuiz = () => {
  const [phase, setPhase] = useState("quiz"); // quiz | result | form | done
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const step = QUIZ_STEPS[stepIndex];
  const totalSteps = QUIZ_STEPS.length;

  const selectSingle = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));
  const toggleMulti = (qid, value) =>
    setAnswers((a) => {
      const cur = Array.isArray(a[qid]) ? a[qid] : [];
      return { ...a, [qid]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] };
    });

  const stepAnswered = step.questions.every((q) => {
    const v = answers[q.id];
    return q.type === "multi" ? Array.isArray(v) && v.length > 0 : !!v;
  });

  const next = () => {
    if (stepIndex < totalSteps - 1) return setStepIndex((i) => i + 1);
    setResult(scoreAnswers(answers));
    setPhase("result");
  };
  const back = () => setStepIndex((i) => Math.max(0, i - 1));

  const pkg = result ? PACKAGE_RESULTS[result.packageKey] : null;

  return (
    <section data-testid="package-quiz-section" id="package-quiz" className="relative py-16 sm:py-24 bg-[#09090b]">
      <div className="mx-auto w-full max-w-[760px] px-[5vw]">
        <div className="text-center mb-10 sm:mb-14">
          <p className="font-mono-grotesk text-[11px] tracking-[0.3em] uppercase text-white/40 mb-4">
            <span className="inline-block h-px w-8 align-middle mr-3 bg-white/30" />
            Not sure which package fits?
          </p>
          <h2 className="font-title font-medium text-white tracking-tight leading-[1.1] text-3xl sm:text-4xl lg:text-5xl">
            Find Your Perfect Website Package
          </h2>
        </div>

        <div className="rounded-[24px] border border-white/[0.08] bg-white/[0.015] p-6 sm:p-10">
          <AnimatePresence mode="wait">
            {phase === "quiz" && (
              <motion.div
                key={`step-${stepIndex}`}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.4, ease }}
              >
                {/* Progress */}
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono-grotesk text-[10.5px] tracking-[0.2em] uppercase text-white/45">
                    Step {stepIndex + 1} of {totalSteps}
                  </span>
                  <span className="font-mono-grotesk text-[10.5px] tracking-[0.2em] uppercase text-white/45">
                    {step.title}
                  </span>
                </div>
                <div className="flex gap-1.5 mb-8">
                  {QUIZ_STEPS.map((_, i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-colors duration-500 ${
                        i <= stepIndex ? "bg-white" : "bg-white/12"
                      }`}
                    />
                  ))}
                </div>

                <div className="space-y-8">
                  {step.questions.map((q) => (
                    <div key={q.id}>
                      <p className="font-title text-lg sm:text-xl font-medium text-white tracking-tight">{q.label}</p>
                      {q.hint && <p className="mt-1 text-[12.5px] text-white/40">{q.hint}</p>}
                      <div className="mt-4 space-y-2.5">
                        {q.options.map((o) => {
                          const sel =
                            q.type === "multi"
                              ? Array.isArray(answers[q.id]) && answers[q.id].includes(o.value)
                              : answers[q.id] === o.value;
                          return (
                            <OptionCard
                              key={o.value}
                              testid={`quiz-opt-${q.id}-${o.value}`}
                              selected={sel}
                              label={o.label}
                              onClick={() => (q.type === "multi" ? toggleMulti(q.id, o.value) : selectSingle(q.id, o.value))}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-9 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    data-testid="quiz-back"
                    onClick={back}
                    disabled={stepIndex === 0}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 h-11 px-5 text-[13px] text-white/70 hover:text-white hover:border-white/35 transition-colors disabled:opacity-30 disabled:pointer-events-none"
                  >
                    <ArrowLeft size={15} /> Back
                  </button>
                  <button
                    type="button"
                    data-testid="quiz-continue"
                    onClick={next}
                    disabled={!stepAnswered}
                    className="inline-flex items-center gap-2 rounded-full bg-white text-black h-11 px-6 text-[13.5px] font-medium hover:bg-white/90 transition-all disabled:opacity-30 disabled:pointer-events-none"
                  >
                    {stepIndex === totalSteps - 1 ? "See my result" : "Continue"} <ArrowRight size={15} />
                  </button>
                </div>
              </motion.div>
            )}

            {(phase === "result" || phase === "form") && pkg && (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease }}
                className="text-center"
                data-testid="quiz-result"
              >
                <div className="grid place-items-center">
                  <motion.span
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.6, ease, delay: 0.1 }}
                    className="grid place-items-center h-14 w-14 rounded-full bg-white text-black mb-6"
                  >
                    <CheckCircle2 size={28} />
                  </motion.span>
                </div>
                <p className="font-mono-grotesk text-[10.5px] tracking-[0.25em] uppercase text-white/45">
                  Your Recommended Website Package
                </p>
                <h3 data-testid="quiz-result-name" className="mt-3 font-title font-semibold text-white tracking-tight text-4xl sm:text-5xl">
                  {pkg.name}
                </h3>
                <p className="mt-1.5 font-title text-xl text-white/70">{pkg.price}</p>
                <p className="mt-6 max-w-lg mx-auto text-[15px] text-white/60 leading-relaxed">{pkg.description}</p>

                {phase === "result" && (
                  <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <button
                      type="button"
                      data-testid="quiz-get-audit"
                      onClick={() => setPhase("form")}
                      className="inline-flex items-center gap-2 rounded-full bg-white text-black h-12 px-7 text-[14px] font-medium hover:bg-white/90 transition-colors"
                    >
                      Get My Personalised Website Audit
                    </button>
                    <a
                      href="/#contact"
                      className="inline-flex items-center gap-2 rounded-full border border-white/15 h-12 px-7 text-[14px] text-white hover:border-white/40 transition-colors"
                    >
                      Book a call
                    </a>
                  </div>
                )}

                {phase === "form" && <QuizForm answers={answers} result={result} onDone={() => setPhase("done")} />}
              </motion.div>
            )}

            {phase === "done" && (
              <motion.div
                key="done"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease }}
                className="text-center py-6"
                data-testid="quiz-done"
              >
                <div className="grid place-items-center">
                  <span className="grid place-items-center h-14 w-14 rounded-full bg-white text-black mb-6">
                    <CheckCircle2 size={28} />
                  </span>
                </div>
                <h3 className="font-title font-semibold text-white tracking-tight text-3xl sm:text-4xl">
                  Thank you — it's on its way.
                </h3>
                <p className="mt-4 max-w-md mx-auto text-[15px] text-white/60 leading-relaxed">
                  I'll personally review your website and send your audit within 48 hours. Want to talk sooner?
                </p>
                <a
                  href="/#contact"
                  data-testid="quiz-done-book"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-black h-12 px-7 text-[14px] font-medium hover:bg-white/90 transition-colors"
                >
                  Book a call
                </a>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default PackageQuiz;
